import React from 'react';
import { Card, FullListIcon, Table } from '@avila-tek/ui';
import { createColumnHelper } from '@tanstack/react-table';
import { Hit } from '@algolia/client-search';
import { ApolloQueryResult, OperationVariables } from '@apollo/client';
import { OrderPayment, PageInfo, PayableRequestInfo } from '../../../models';
import { ActiveDotDynamicCell } from '../../shared';
import { PAYMENT_STATUS_COLOR } from '../../../constants/constants';
import {
  useAlgoliaSearch,
  usePayableDetailsTable,
  useUser,
} from '../../../hooks';
import { roles } from '../../../static';
import { findItemByText } from '../../../lib';

const columnHelper = createColumnHelper<OrderPayment>();

interface PayableDetailsTableProps {
  data: OrderPayment[];
  restarValues: boolean;
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenReport?: React.Dispatch<React.SetStateAction<boolean>>;
  setAmount?: React.Dispatch<React.SetStateAction<number>>;
  setOrderProductsIds: React.Dispatch<React.SetStateAction<string[]>>;
  refetch: (variables?: Partial<OperationVariables>) => Promise<
    ApolloQueryResult<{
      account: PayableRequestInfo;
    }>
  >;
}

function PayableDetailsTable({
  data,
  restarValues,
  setIsOpen,
  setIsOpenReport,
  setAmount,
  setOrderProductsIds,
  refetch,
}: PayableDetailsTableProps) {
  const [pageInfoAlgolia, setPageInfoAlgolia] = React.useState<PageInfo>({
    currentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    pageCount: 0,
    perPage: 10,
  });

  const [user] = useUser();
  const {
    // States
    orderPayments,
    payablesData,
    setPayablesData,
    searchText,
    setSearchText,

    // Handle functions
    onClickRow,
    handleCheck,
    handlePayAll,
  } = usePayableDetailsTable({
    data,
    restarValues,
    setIsOpen,
    setIsOpenReport,
    setAmount,
    setOrderProductsIds,
  });

  const selectableColumns = [
    {
      header: 'Seleccionar',
      cell: (info) => {
        const isChecked = orderPayments.some(
          (item) => item._id === info.row.original.orderProduct._id
        );
        return info.row.original.paymentStatus !== 'paid' ? (
          <input
            type="checkbox"
            checked={isChecked}
            className="w-4 h-4 mt-1 cursor-pointer bg-white rounded border-purple-200 focus:ring-purple-300 focus:ring-2"
            onClick={(e) => {
              handleCheck(info.row.original);
              e.stopPropagation();
            }}
          />
        ) : null;
      },
    },
  ];

  const columns = [
    // We show the selectable columns only if the user is not an ally
    ...(user.userType !== roles.ally ? selectableColumns : []),
    columnHelper.accessor('orderProduct', {
      header: 'Producto',
      cell: (info) => (
        <div className="flex flex-col items-start ">
          <p className="font-bold">
            {info.row.original.orderProduct.product.title}
          </p>
          <p className="text-gray-500">
            {info.row.original.orderProduct.variant.sku}
          </p>
        </div>
      ),
    }),

    columnHelper.accessor('paymentStatus', {
      header: 'Estatus',
      cell: (info) => (
        <ActiveDotDynamicCell
          text={info.row.original.paymentStatus}
          opctions={PAYMENT_STATUS_COLOR}
        />
      ),
    }),

    columnHelper.accessor('paymentStatus', {
      header: 'Saldo pendiente',
      cell: (info) => (
        <p>$ {info.row.original.orderProduct.variant.agreedPrice}</p>
      ),
    }),
  ];

  const { fetchData } = useAlgoliaSearch<OrderPayment>({
    pageInfo: pageInfoAlgolia,
    setPageInfo: setPageInfoAlgolia,
    restrictSearchableAttributes: [
      'orderPayments.paymentStatus',
      'orderPayments.orderProduct.variant.price',
      'orderPayments.orderProduct.product.title',
      'orderPayments.orderProduct.product.options.values',
    ],
    index: 'accounts',
    filters: `ally.id:${data[0]?.orderProduct?.company}`,
  });

  const memoizedSyncData = React.useCallback(async () => {
    if (searchText) {
      const { pageInfo, items } = await fetchData({
        searchText: searchText.toString(),
        newPage: pageInfoAlgolia.currentPage,
      });
      const orders = (items as OrderPayment[])[0]?.orderPayments ?? [];
      const filterOrders = findItemByText(
        orders,
        searchText as string
      ) as Hit<OrderPayment>[];
      setPayablesData(filterOrders);
      setPageInfoAlgolia(pageInfo);
    } else {
      // Llama a refetch y espera su resultado
      const { data: refetchedData } = await refetch();

      if (refetchedData) {
        setPayablesData(refetchedData?.account?.orderPayments ?? []);
      }
    }
  }, [searchText, pageInfoAlgolia?.currentPage, refetch]);

  React.useEffect(() => {
    memoizedSyncData();
  }, [searchText, memoizedSyncData]);

  return (
    <Card className="overflow-x-auto p-5">
      <Table<OrderPayment> data={payablesData} columns={columns} href="#">
        <Table.Header className="p-6 w-full flex justify-between">
          <Table.SearchInput
            placeholder="Buscar por producto"
            manualFilter
            value={searchText.toString()}
            onChange={(value) => {
              setSearchText(value);
            }}
          />
          <div className="relative">
            {data.length > 0 ? (
              <button
                type="button"
                className="absolute w-6 right-2 top-2 text-gray-500 hover:text-gray-700"
                onClick={() => setIsOpenReport((prev) => !prev)}
              >
                <FullListIcon />
              </button>
            ) : null}
            <div className="mr-11">
              {data.length > 0 && user.userType !== roles.ally ? (
                <Table.AddButton
                  title={
                    orderPayments.length > 0
                      ? 'Pagar seleccionados'
                      : 'Pagar todo'
                  }
                  action={() => handlePayAll()}
                />
              ) : null}
            </div>
          </div>
        </Table.Header>
        <Table.Content<OrderPayment> onClickRow={onClickRow} />
        <Table.Footer>
          <Table.Pagination className="ml-auto" />
        </Table.Footer>
      </Table>
    </Card>
  );
}

export default PayableDetailsTable;
